import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Gavel, 
  TrendingUp, 
  Clock, 
  Shield, 
  Award, 
  FileText, 
  Briefcase,
  Target,
  Leaf,
  Fish,
  TreePine,
  Zap,
  Droplets,
  Mountain,
  Filter,
  Search,
  SortAsc,
  Lock,
  Eye,
  ShoppingCart,
  Users
} from 'lucide-react';
import { useCurrency } from '@/hooks/useCurrency';

interface MarketplaceListing {
  id: string;
  projectId: string;
  projectName: string;
  location: string;
  imageUrl: string;
  assetType: string;
  quantity: number;
  pricePerCredit: number;
  listingType: 'fixed' | 'auction';
  minimumPurchase: number;
  vintage: number;
  certifications: string[];
  sellerId: string;
  sellerName: string;
  escrowStatus: 'locked' | 'pending' | 'released';
  bidsCount?: number;
  timeRemaining?: string;
  biodiversityScore?: number;
  communityImpact?: string;
  carbonIntensity?: number;
  cobenefits: string[];
  documentationUrl?: string;
  verificationStatus: 'verified' | 'pending' | 'unverified';
  featured: boolean;
  createdAt: string;
}

const mockListings: MarketplaceListing[] = [
  {
    id: '1',
    projectId: 'proj-001',
    projectName: 'Gujarat Wind Farm Project',
    location: 'Gujarat, India',
    imageUrl: 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    assetType: 'carbon_credits',
    quantity: 5000,
    pricePerCredit: 52,
    listingType: 'fixed',
    minimumPurchase: 100,
    vintage: 2023,
    certifications: ['VCS', 'Gold Standard'],
    sellerId: 'seller-1',
    sellerName: 'Gujarat Wind Energy Pvt Ltd',
    escrowStatus: 'locked',
    biodiversityScore: 85,
    communityImpact: 'Provides clean energy to 15,000 households',
    carbonIntensity: 0.85,
    cobenefits: ['Clean Energy', 'Job Creation', 'Air Quality'],
    documentationUrl: '#',
    verificationStatus: 'verified',
    featured: true,
    createdAt: '2024-01-15'
  },
  {
    id: '2',
    projectId: 'proj-002',
    projectName: 'Kerala Forest Conservation',
    location: 'Kerala, India',
    imageUrl: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    assetType: 'biodiversity_credits',
    quantity: 2500,
    pricePerCredit: 75,
    listingType: 'auction',
    minimumPurchase: 50,
    vintage: 2024,
    certifications: ['VCS', 'CCB', 'FSC'],
    sellerId: 'seller-2',
    sellerName: 'Kerala Forest Department',
    escrowStatus: 'locked',
    bidsCount: 8,
    timeRemaining: '2d 14h',
    biodiversityScore: 98,
    communityImpact: 'Protects habitat for 200+ species',
    cobenefits: ['Biodiversity', 'Carbon Sequestration', 'Community Livelihoods'],
    documentationUrl: '#',
    verificationStatus: 'verified',
    featured: false,
    createdAt: '2024-02-10'
  },
  {
    id: '3',
    projectId: 'proj-003',
    projectName: 'Rajasthan Solar Installation',
    location: 'Rajasthan, India',
    imageUrl: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    assetType: 'renewable_energy_certificates',
    quantity: 8000,
    pricePerCredit: 45,
    listingType: 'fixed',
    minimumPurchase: 200,
    vintage: 2024,
    certifications: ['I-REC', 'Green-e'],
    sellerId: 'seller-3',
    sellerName: 'Rajasthan Solar Co.',
    escrowStatus: 'locked',
    carbonIntensity: 0.02,
    communityImpact: 'Creates 500+ local jobs',
    cobenefits: ['Clean Energy', 'Grid Stability', 'Rural Development'],
    documentationUrl: '#',
    verificationStatus: 'verified',
    featured: false,
    createdAt: '2024-03-05'
  }
];

const assetTypeConfig = {
  carbon_credits: { icon: TreePine, color: 'hsl(var(--success))', name: 'Carbon Credits' },
  renewable_energy_certificates: { icon: Zap, color: 'hsl(var(--warning))', name: 'RECs' },
  biodiversity_credits: { icon: Fish, color: 'hsl(var(--secondary))', name: 'Biodiversity' },
  water_credits: { icon: Droplets, color: 'hsl(var(--primary))', name: 'Water Credits' },
  low_methane_gas_credits: { icon: Mountain, color: 'hsl(var(--info))', name: 'Low-Methane Gas' }
};

export const ProfessionalMarketplace = () => {
  const [activeTab, setActiveTab] = useState('browse');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortBy, setSortBy] = useState('price_asc');
  const [selectedListing, setSelectedListing] = useState<MarketplaceListing | null>(null);
  const { formatPrice } = useCurrency();

  const filteredListings = mockListings.filter(listing => {
    const matchesSearch = listing.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         listing.location.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || listing.assetType === filterType;
    return matchesSearch && matchesType;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'price_asc':
        return a.pricePerCredit - b.pricePerCredit;
      case 'price_desc':
        return b.pricePerCredit - a.pricePerCredit;
      case 'quantity_desc':
        return b.quantity - a.quantity;
      case 'newest':
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      default:
        return 0;
    }
  });

  const getStatusBadge = (listing: MarketplaceListing) => {
    if (listing.listingType === 'auction') {
      return (
        <Badge variant="secondary" className="bg-gradient-to-r from-info/20 to-info/10 text-info border-info/30">
          <Gavel className="w-3 h-3 mr-1" />
          Live Auction
        </Badge>
      );
    }
    return (
      <Badge variant="secondary" className="bg-gradient-to-r from-primary/20 to-primary/10 text-primary border-primary/30">
        <TrendingUp className="w-3 h-3 mr-1" />
        Fixed Price
      </Badge>
    );
  };

  const getEscrowIndicator = (status: string) => {
    return (
      <div className="flex items-center gap-1 text-xs text-muted-foreground">
        <Shield className="w-3 h-3" />
        <span>Registry Escrow: {status.charAt(0).toUpperCase() + status.slice(1)}</span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Professional Marketplace</h1>
          <p className="text-muted-foreground">Trade verified environmental assets with registry-coupled escrow</p>
        </div>
        <Button className="bg-gradient-primary text-primary-foreground">
          <ShoppingCart className="w-4 h-4 mr-2" />
          Create Listing
        </Button>
      </div>

      {/* Market Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active Listings</p>
                <p className="text-2xl font-bold">{mockListings.length}</p>
              </div>
              <Gavel className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Volume</p>
                <p className="text-2xl font-bold">{mockListings.reduce((sum, l) => sum + l.quantity, 0).toLocaleString()}</p>
              </div>
              <TrendingUp className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Avg. Price</p>
                <p className="text-2xl font-bold">{formatPrice(mockListings.reduce((sum, l) => sum + l.pricePerCredit, 0) / mockListings.length)}</p>
              </div>
              <Award className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">In Escrow</p>
                <p className="text-2xl font-bold">{formatPrice(mockListings.filter(l => l.escrowStatus === 'locked').reduce((sum, l) => sum + (l.quantity * l.pricePerCredit), 0))}</p>
              </div>
              <Lock className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="browse">Browse Listings</TabsTrigger>
          <TabsTrigger value="auctions">Live Auctions</TabsTrigger>
          <TabsTrigger value="create-auction">Create Auction</TabsTrigger>
          <TabsTrigger value="my-proposals">My Proposals & RFPs</TabsTrigger>
        </TabsList>

        <TabsContent value="browse" className="space-y-6">
          {/* Search and Filters */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <Label htmlFor="search">Search Projects</Label>
                  <div className="relative mt-1">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="search"
                      placeholder="Search by project name or location..."
                      className="pl-10"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <Label>Asset Type</Label>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-[200px] mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="carbon_credits">Carbon Credits</SelectItem>
                      <SelectItem value="renewable_energy_certificates">RECs</SelectItem>
                      <SelectItem value="biodiversity_credits">Biodiversity</SelectItem>
                      <SelectItem value="water_credits">Water Credits</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Sort By</Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger className="w-[180px] mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="price_asc">Price: Low to High</SelectItem>
                      <SelectItem value="price_desc">Price: High to Low</SelectItem>
                      <SelectItem value="quantity_desc">Quantity: High to Low</SelectItem>
                      <SelectItem value="newest">Newest First</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Listings Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredListings.map((listing) => {
              const assetConfig = assetTypeConfig[listing.assetType as keyof typeof assetTypeConfig];
              const Icon = assetConfig?.icon || TreePine;

              return (
                <Card key={listing.id} className="group hover:shadow-elegant transition-all duration-300 overflow-hidden">
                  {/* Featured/Status Banner */}
                  {listing.featured && (
                    <div className="bg-gradient-primary text-primary-foreground px-4 py-2 text-sm font-medium">
                      <div className="flex items-center gap-2">
                        <Award className="w-4 h-4" />
                        Featured Listing
                      </div>
                    </div>
                  )}

                  {/* Escrow Status Banner */}
                  <div className="bg-gradient-to-r from-info/20 to-info/10 border-b border-info/20 px-4 py-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-info text-sm font-medium">
                        <Lock className="w-4 h-4" />
                        Registry Escrow: Secured
                      </div>
                      {getStatusBadge(listing)}
                    </div>
                  </div>

                  {/* Project Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={listing.imageUrl} 
                      alt={listing.projectName}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    
                    {/* Asset Type Badge */}
                    <div className="absolute top-4 left-4">
                      <Badge variant="secondary" className="bg-background/90 backdrop-blur-sm">
                        <Icon className="w-3 h-3 mr-1" style={{ color: assetConfig?.color }} />
                        {assetConfig?.name}
                      </Badge>
                    </div>

                    {/* Verification Status */}
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-success/90 text-success-foreground">
                        <Shield className="w-3 h-3 mr-1" />
                        Verified
                      </Badge>
                    </div>

                    {/* Auction Timer */}
                    {listing.listingType === 'auction' && listing.timeRemaining && (
                      <div className="absolute bottom-4 left-4">
                        <Badge variant="secondary" className="bg-warning/90 text-warning-foreground">
                          <Clock className="w-3 h-3 mr-1" />
                          {listing.timeRemaining}
                        </Badge>
                      </div>
                    )}

                    {/* Price */}
                    <div className="absolute bottom-4 right-4">
                      <div className="bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2">
                        <p className="text-sm font-medium">{formatPrice(listing.pricePerCredit)}/credit</p>
                      </div>
                    </div>
                  </div>

                  <CardContent className="p-6 space-y-4">
                    {/* Project Details */}
                    <div>
                      <h3 className="font-semibold text-lg mb-1">{listing.projectName}</h3>
                      <p className="text-sm text-muted-foreground">{listing.location}</p>
                    </div>

                    {/* Quantity & Seller */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Quantity:</span>
                      <span className="font-medium">{listing.quantity.toLocaleString()} credits</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Seller:</span>
                      <span className="font-medium">{listing.sellerName}</span>
                    </div>

                    {/* Co-benefits */}
                    <div>
                      <p className="text-xs text-muted-foreground mb-2">Co-benefits:</p>
                      <div className="flex flex-wrap gap-1">
                        {listing.cobenefits.slice(0, 3).map((benefit) => (
                          <Badge key={benefit} variant="outline" className="text-xs">
                            {benefit}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Impact Metrics */}
                    {(listing.biodiversityScore || listing.communityImpact) && (
                      <div className="grid grid-cols-1 gap-2">
                        {listing.biodiversityScore && (
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">Biodiversity Score:</span>
                            <span className="font-medium">{listing.biodiversityScore}/100</span>
                          </div>
                        )}
                        {listing.communityImpact && (
                          <p className="text-xs text-muted-foreground">{listing.communityImpact}</p>
                        )}
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-2 pt-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                        onClick={() => setSelectedListing(listing)}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="flex-1"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Documentation
                      </Button>
                    </div>

                    {/* Documentation Link */}
                    {listing.documentationUrl && (
                      <Button variant="ghost" size="sm" className="w-full text-xs">
                        <FileText className="w-3 h-3 mr-2" />
                        View Project Documentation
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="auctions">
          <div className="text-center py-12">
            <Gavel className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Live Auctions</h3>
            <p className="text-muted-foreground">
              Participate in live auctions for premium environmental assets
            </p>
          </div>
        </TabsContent>

        <TabsContent value="create-auction" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Gavel className="w-5 h-5" />
                  Create Live Auction
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label>Select Project</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Choose project to auction" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="proj-001">Gujarat Solar Farm</SelectItem>
                        <SelectItem value="proj-002">Rajasthan Wind Farm</SelectItem>
                        <SelectItem value="proj-003">Maharashtra Biogas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Credits to Auction</Label>
                    <Input type="number" placeholder="Number of credits" />
                  </div>
                  <div>
                    <Label>Reserve Price (₹/credit)</Label>
                    <Input type="number" placeholder="Minimum acceptable price" />
                  </div>
                  <div>
                    <Label>Auction Duration</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Select duration" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="24">24 hours</SelectItem>
                        <SelectItem value="48">48 hours</SelectItem>
                        <SelectItem value="72">72 hours</SelectItem>
                        <SelectItem value="168">7 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button className="w-full">
                    <Gavel className="w-4 h-4 mr-2" />
                    Launch Auction
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Auction Preview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8">
                  <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Configure auction details to see preview</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="my-proposals" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>My Proposals & RFPs</CardTitle>
              <p className="text-sm text-muted-foreground">
                Track your submitted proposals and received RFPs
              </p>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="proposals">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="proposals">My Proposals</TabsTrigger>
                  <TabsTrigger value="rfps">Available RFPs</TabsTrigger>
                </TabsList>
                
                <TabsContent value="proposals" className="mt-6">
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No proposals submitted yet</p>
                  </div>
                </TabsContent>
                
                <TabsContent value="rfps" className="mt-6">
                  <div className="text-center py-8">
                    <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No RFPs available</p>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};